import type { ReactNode } from "react";

type Props = {
  followUp: ReactNode;
  calendar: ReactNode;
  dealTodos: ReactNode;
  stickyNotes: ReactNode;
  bulkCapture: ReactNode;
  rtoCalculator: ReactNode;
};

/**
 * Sales Console layout — mobile (`<lg`) stacks all 5 sections
 * vertically with `space-y-6`. Desktop (`lg:+`) splits into an even
 * 6/6 grid.
 *
 * **Left (lead-specific workflow surfaces):** Follow-up triage queue
 * → Customer Calendar → Deal todos → Bulk Lead Capture. All four are
 * lead-centric ("who do I follow up with today, what's on the
 * calendar, what post-sale steps are open, what raw notes need to be
 * converted into leads") and read top-to-bottom as a daily-flow column.
 *
 * **Right (org / general surfaces):** Sales Console Sticky Notes →
 * RTO Calculator. Notes are org-wide guidance from owners /
 * trainers (not lead-specific), and the RTO calculator is a
 * general-purpose tool that runs the same regardless of lead state.
 * Stacking them together puts the "things I reference, not act on"
 * surfaces in their own column.
 *
 * Reorg history: the original layout was a 5/7 split with the
 * follow-up queue alone in a sticky left rail and everything else
 * crammed into the wider right column (dev-sync note bbf54838 flagged
 * "one item on the left, everything else on the right"). The
 * follow-up commit rebalanced to an even 6/6 with lead-specific
 * surfaces on the left and the RTO calculator alone on the right,
 * pairing Sticky Notes + Bulk Capture into an `xl:grid-cols-2` pair
 * on the left under the calendar. Dev-sync note 22033300 then
 * flagged that Sticky Notes is *org-general* (not lead-specific) and
 * proposed splitting it off to the right column above the RTO
 * calculator. That's this iteration. The xl:+ 2-up pair is retired
 * (Bulk Capture takes the full left-column width below the
 * calendar); both columns continue to scroll naturally with no
 * sticky positioning.
 *
 * Extracted from `SalesConsolePanel.tsx` to keep that file under the
 * 300-line hard threshold from `.cursor/rules/file-size-hygiene.mdc`.
 */
export function SalesConsoleDesktopLayout(props: Props) {
  return (
    <div className="space-y-6 text-left lg:grid lg:grid-cols-2 lg:gap-6 lg:space-y-0 lg:items-start">
      <div className="space-y-6">
        {props.followUp}
        {props.calendar}
        {props.dealTodos}
        {props.bulkCapture}
      </div>
      <div className="space-y-6">
        {props.stickyNotes}
        {props.rtoCalculator}
      </div>
    </div>
  );
}
